import { useEffect } from 'react';
import { isNativeToWeb, type NativeToWeb, type WebToNative } from '@heviton/bridge-protocol';

/**
 * Native bridge transport — `window.__HEVITON_BRIDGE__`.
 *
 * 노출 surface 는 namespaced `__HEVITON_BRIDGE__` 만 사용 (기존 `window.ReactNativeWebView` 대체).
 * native module (`@heviton/webview`) 의 user script 또는 JavascriptInterface 가 이 객체를 주입.
 *
 * 자세한 transport 매핑은 [`docs/adr/webview-bundling-and-routing.md`](../../../../../docs/adr/webview-bundling-and-routing.md).
 */
interface HevitonBridge {
  send: (msg: string) => void;
}

declare global {
  interface Window {
    __HEVITON_BRIDGE__?: HevitonBridge;
  }
}

export function isNative(): boolean {
  return typeof window !== 'undefined' && !!window.__HEVITON_BRIDGE__;
}

export function sendToNative(msg: WebToNative): void {
  if (!isNative()) {
    console.warn('[bridge] sendToNative called in non-native env', msg);
    return;
  }
  window.__HEVITON_BRIDGE__!.send(JSON.stringify(msg));
}

// ---------------------------------------------------------------------------
// Native → Web 라우팅
// ---------------------------------------------------------------------------

type NativeEventHandler<T extends NativeToWeb['type']> = (
  msg: Extract<NativeToWeb, { type: T }>,
) => void;
// 내부 저장 시점에는 유니온 타입으로 wide. 등록 시점에 narrow한 NativeEventHandler<T>를 받아 캐스트.
type AnyNativeEventHandler = NativeEventHandler<NativeToWeb['type']>;

const eventListeners = new Map<NativeToWeb['type'], Set<AnyNativeEventHandler>>();
const pendingRequests = new Map<
  string,
  { resolve: (data: unknown) => void; reject: (e: Error) => void; timer: number }
>();

let dispatcherInstalled = false;

function dispatch(raw: unknown): void {
  if (!isNativeToWeb(raw)) {
    console.warn('[bridge] invalid message from native', raw);
    return;
  }
  const msg = raw;

  if (msg.type === 'response') {
    const pending = pendingRequests.get(msg.reqId);
    if (!pending) return;
    pendingRequests.delete(msg.reqId);
    window.clearTimeout(pending.timer);
    if (msg.ok) pending.resolve(msg.data);
    else pending.reject(new Error(msg.error));
    return;
  }

  const listeners = eventListeners.get(msg.type);
  if (!listeners) return;
  for (const l of listeners) {
    try {
      (l as NativeEventHandler<typeof msg.type>)(msg);
    } catch (e) {
      console.error('[bridge] listener threw', e);
    }
  }
}

function installDispatcher(): void {
  if (dispatcherInstalled || typeof window === 'undefined') return;
  // bridge 는 native ↔ web 통신 전용. 비-native 에서 listener 등록 시 브라우저 확장
  // (React DevTools 등) 의 postMessage 트래픽까지 모두 받아 검증 실패 경고를 폭증시킴.
  if (!isNative()) return;
  dispatcherInstalled = true;

  const onMessage = (event: MessageEvent) => {
    let parsed: unknown;
    try {
      parsed = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }
    dispatch(parsed);
  };

  window.addEventListener('message', onMessage);
  // native module 일부 환경에서는 document로 message를 디스패치 — 양쪽 모두 청취.
  document.addEventListener('message', onMessage as unknown as EventListener);
}

installDispatcher();

// ---------------------------------------------------------------------------
// 요청/응답 (reqId 매칭)
// ---------------------------------------------------------------------------

export interface BridgeRequestOptions {
  timeoutMs?: number;
}

let reqIdSeq = 0;
function nextReqId(): string {
  reqIdSeq += 1;
  return `r${Date.now().toString(36)}.${reqIdSeq}`;
}

type RequestableMessage = Extract<WebToNative, { reqId: string }>;
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
export type BridgeRequestInput = DistributiveOmit<RequestableMessage, 'reqId'>;

export function bridgeRequest<T = unknown>(
  msg: BridgeRequestInput,
  opts: BridgeRequestOptions = {},
): Promise<T> {
  if (!isNative()) {
    return Promise.reject(new Error('bridge.request called in non-native env'));
  }
  const reqId = nextReqId();
  const fullMsg = { ...msg, reqId } as RequestableMessage;
  const timeoutMs = opts.timeoutMs ?? 5000;

  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      pendingRequests.delete(reqId);
      reject(new Error(`bridge request timeout: ${msg.type}`));
    }, timeoutMs);
    pendingRequests.set(reqId, {
      resolve: (data) => resolve(data as T),
      reject,
      timer,
    });
    sendToNative(fullMsg);
  });
}

// ---------------------------------------------------------------------------
// 이벤트 리스너
// ---------------------------------------------------------------------------

export function registerNativeListener<T extends NativeToWeb['type']>(
  type: T,
  handler: NativeEventHandler<T>,
): () => void {
  let set = eventListeners.get(type);
  if (!set) {
    set = new Set();
    eventListeners.set(type, set);
  }
  set.add(handler as AnyNativeEventHandler);
  return () => {
    set!.delete(handler as AnyNativeEventHandler);
  };
}

export function useNativeEvent<T extends NativeToWeb['type']>(
  type: T,
  handler: NativeEventHandler<T>,
): void {
  useEffect(() => registerNativeListener(type, handler), [type, handler]);
}

// ---------------------------------------------------------------------------
// app.ready — 첫 부팅 race 방지용 신호
// ---------------------------------------------------------------------------

let appReadySent = false;
export function setAppReady(): void {
  if (appReadySent || !isNative()) return;
  appReadySent = true;
  sendToNative({ type: 'app.ready' });
}
